import { useNavState } from '@/providers/NavStateProvider';
import { useEffect, useState } from 'react';
import { ScrollTop } from '@/components/element/ScrollTop';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useInvalidateWeeklySummaries, useWeeklySummaries } from '@/hooks/api/2390_weekly_summaries';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { SingleValue } from 'react-select';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { SelectSingle } from '@/components/element/Form/SelectSingle';
import { SalesOrderWeeklySummaryComparison, WeeklySummary } from '@/hooks/api/models';
import moment from 'moment/moment';
import { useWeeklySummaryComparison, useWeeklySummaryComparison2 } from '@/hooks/api/2400_weekly_summary_comparison';
import { useLoading } from '@/providers/LoadingProvider';
import { ComparisonSummaries } from '@/features/w/[identifier]/dashboard/weekly_comparison/{components}/ComparisonSummaries';
import { ComparisonPhaseWeeklySummaries } from '@/features/w/[identifier]/dashboard/weekly_comparison/{components}/ComparisonPhaseWeeklySummaries';
import { FilterModal } from '@/features/w/[identifier]/dashboard/weekly_comparison/{components}/FilterModal';
import { SearchConditionTags } from '@/features/w/[identifier]/dashboard/weekly_comparison/{components}/SearchConditionTags';
import { useCreateWeeklySummary } from '@/hooks/api/2410_create_weekly_summary';
import { toastApiError, toastSuccess } from '@/lib/toast';
import { useDashboardSearchCondition } from '@/providers/DashboardSearchConditionProvider';

export const WeeklyComparison = () => {
  const { setTitle, setBreadcrumbs } = useNavState();

  useEffect(() => setTitle('比較ボード'), []);
  useEffect(() => setBreadcrumbs([]), []);

  const { _w_ } = useCurrentWorkspace();
  const { setShowLoading } = useLoading();
  const { data: weeklySummaries } = useWeeklySummaries({ identifier: _w_!.identifier });
  const {
    weeklyComparisonDashboardSearchCondition: searchCondition,
    setWeeklyComparisonDashboardSearchCondition: setSearchCondition,
    weeklyComparisonCollapsedCondition: collapsedCondition,
    setWeeklyComparisonCollapsedCondition: setCollapsedCondition,
  } = useDashboardSearchCondition();
  const {
    data: weeklySummaryComparison,
    isLoading,
    refetch,
  } = useWeeklySummaryComparison({
    ...searchCondition,
    identifier: _w_!.identifier,
    enabled: false,
  });

  useEffect(() => setShowLoading(isLoading), [isLoading]);

  useEffect(() => {
    if (weeklySummaries && weeklySummaries.length > 1) {
      const after_weekly_summary_id =
        searchCondition.after_weekly_summary_id != 0 ? searchCondition.after_weekly_summary_id : weeklySummaries[0].id;
      const before_weekly_summary_id =
        searchCondition.before_weekly_summary_id != 0
          ? searchCondition.before_weekly_summary_id
          : weeklySummaries[1].id;
      setSearchCondition({
        ...searchCondition,
        after_weekly_summary_id,
        before_weekly_summary_id,
      });
    }
  }, [weeklySummaries]);

  useEffect(() => {
    if (searchCondition.before_weekly_summary_id != 0 && searchCondition.after_weekly_summary_id != 0) {
      refetch();
    }
  }, [searchCondition]);

  useEffect(() => {
    if (weeklySummaryComparison) {
    }
  }, [weeklySummaryComparison]);

  const onBeforeWeeklySummaryIdSelect = (d: SingleValue<SelectOptionType>) => {
    setSearchCondition({ ...searchCondition, before_weekly_summary_id: d != null ? d.value : 0 });
  };
  const onAfterWeeklySummaryIdSelect = (d: SingleValue<SelectOptionType>) => {
    setSearchCondition({ ...searchCondition, after_weekly_summary_id: d != null ? d.value : 0 });
  };

  return (
    <WorkspaceLayout>
      <ScrollTop />

      <div className="container-fluid py-4">
        <CreateWeeklySummaryButton />

        <div className="row">
          <div className="col-12">
            <div className="card mb-4">
              <div className="card-body p-3">
                <form className="row">
                  <div className="col-12 col-md-4">
                    <div className="form-group flex-1 me-2">
                      <label>比較データ（古）</label>
                      <WeeklySummarySelect
                        weeklySummaries={weeklySummaries || []}
                        onChange={onBeforeWeeklySummaryIdSelect}
                        selectedId={searchCondition.before_weekly_summary_id}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-group flex-1 me-2">
                      <label>比較データ（新）</label>
                      <WeeklySummarySelect
                        weeklySummaries={weeklySummaries || []}
                        onChange={onAfterWeeklySummaryIdSelect}
                        selectedId={searchCondition.after_weekly_summary_id}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label>&nbsp;</label>
                      <div className="d-flex w-100 align-items-center">
                        <button
                          type="button"
                          className="btn btn-icon-only bg-gradient-dark me-2"
                          data-bs-toggle="modal"
                          data-bs-target="#modal-weekly-comparison-filter"
                        >
                          <i className="fas fa-filter px-2"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </form>

                <SearchConditionTags />
              </div>
            </div>
          </div>

          <FilterModal />

          <ComparisonSummaries
            beforeSummarizedAtLabel={toSummarizedAtLabel(
              weeklySummaries?.find((ws) => ws.id == searchCondition.before_weekly_summary_id)?.summarized_at
            )}
            afterSummarizedAtLabel={toSummarizedAtLabel(
              weeklySummaries?.find((ws) => ws.id == searchCondition.after_weekly_summary_id)?.summarized_at
            )}
            weeklySummaryComparison={weeklySummaryComparison}
          />

          <ComparisonPhaseWeeklySummaries
            phase={'attack_list'}
            phaseLabel={weeklySummaryComparison?.sales_order_phase_labels.attack_list}
            beforeAmount={weeklySummaryComparison?.summaries[0].before_amount}
            afterAmount={weeklySummaryComparison?.summaries[0].after_amount}
            weeklySummaries={weeklySummaryComparison?.attack_list}
            collapsed={collapsedCondition.attackListCollapsed}
            setCollapsed={(collapsed) => {
              setCollapsedCondition({ ...collapsedCondition, attackListCollapsed: collapsed });
            }}
          />

          <ComparisonPhaseWeeklySummaries
            phase={'initial_proposal'}
            phaseLabel={weeklySummaryComparison?.sales_order_phase_labels.initial_proposal}
            beforeAmount={weeklySummaryComparison?.summaries[1].before_amount}
            afterAmount={weeklySummaryComparison?.summaries[1].after_amount}
            weeklySummaries={weeklySummaryComparison?.initial_proposal}
            collapsed={collapsedCondition.initialProposalCollapsed}
            setCollapsed={(collapsed) => {
              setCollapsedCondition({ ...collapsedCondition, initialProposalCollapsed: collapsed });
            }}
          />

          <ComparisonPhaseWeeklySummaries
            phase={'detail_proposal'}
            phaseLabel={weeklySummaryComparison?.sales_order_phase_labels.detail_proposal}
            beforeAmount={weeklySummaryComparison?.summaries[2].before_amount}
            afterAmount={weeklySummaryComparison?.summaries[2].after_amount}
            weeklySummaries={weeklySummaryComparison?.detail_proposal}
            collapsed={collapsedCondition.detailProposalCollapsed}
            setCollapsed={(collapsed) => {
              setCollapsedCondition({ ...collapsedCondition, detailProposalCollapsed: collapsed });
            }}
          />

          <ComparisonPhaseWeeklySummaries
            phase={'under_negotiation'}
            phaseLabel={weeklySummaryComparison?.sales_order_phase_labels.under_negotiation}
            beforeAmount={weeklySummaryComparison?.summaries[3].before_amount}
            afterAmount={weeklySummaryComparison?.summaries[3].after_amount}
            weeklySummaries={weeklySummaryComparison?.under_negotiation}
            collapsed={collapsedCondition.underNegotiationCollapsed}
            setCollapsed={(collapsed) => {
              setCollapsedCondition({ ...collapsedCondition, underNegotiationCollapsed: collapsed });
            }}
          />

          <ComparisonPhaseWeeklySummaries
            phase={'contracting'}
            phaseLabel={weeklySummaryComparison?.sales_order_phase_labels.contracting}
            beforeAmount={weeklySummaryComparison?.summaries[4].before_amount}
            afterAmount={weeklySummaryComparison?.summaries[4].after_amount}
            weeklySummaries={weeklySummaryComparison?.contracting}
            collapsed={collapsedCondition.contractingCollapsed}
            setCollapsed={(collapsed) => {
              setCollapsedCondition({ ...collapsedCondition, contractingCollapsed: collapsed });
            }}
          />

          <ComparisonPhaseWeeklySummaries
            phase={'completed'}
            phaseLabel={weeklySummaryComparison?.sales_order_phase_labels.completed}
            beforeAmount={weeklySummaryComparison?.summaries[5].before_amount}
            afterAmount={weeklySummaryComparison?.summaries[5].after_amount}
            weeklySummaries={weeklySummaryComparison?.completed}
            collapsed={collapsedCondition.completedCollapsed}
            setCollapsed={(collapsed) => {
              setCollapsedCondition({ ...collapsedCondition, completedCollapsed: collapsed });
            }}
          />

          <ComparisonPhaseWeeklySummaries
            phase={'pending'}
            phaseLabel={weeklySummaryComparison?.sales_order_phase_labels.pending}
            beforeAmount={weeklySummaryComparison?.summaries[6].before_amount}
            afterAmount={weeklySummaryComparison?.summaries[6].after_amount}
            weeklySummaries={weeklySummaryComparison?.pending}
            collapsed={collapsedCondition.pendingCollapsed}
            setCollapsed={(collapsed) => {
              setCollapsedCondition({ ...collapsedCondition, pendingCollapsed: collapsed });
            }}
          />

          <ComparisonPhaseWeeklySummaries
            phase={'lost_order'}
            phaseLabel={weeklySummaryComparison?.sales_order_phase_labels.lost_order}
            beforeAmount={weeklySummaryComparison?.summaries[7].before_amount}
            afterAmount={weeklySummaryComparison?.summaries[7].after_amount}
            weeklySummaries={weeklySummaryComparison?.lost_order}
            collapsed={collapsedCondition.lostOrderCollapsed}
            setCollapsed={(collapsed) => {
              setCollapsedCondition({ ...collapsedCondition, lostOrderCollapsed: collapsed });
            }}
          />
        </div>
      </div>
    </WorkspaceLayout>
  );
};

const WeeklySummarySelect = ({
  weeklySummaries,
  onChange,
  selectedId,
}: {
  weeklySummaries: WeeklySummary[];
  onChange: (d: SingleValue<SelectOptionType>) => void;
  selectedId: number;
}) => {
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (weeklySummaries)
      setOptions(
        weeklySummaries.map((d): SelectOptionType => ({ value: d.id, label: toSummarizedAtLabel(d.summarized_at) }))
      );
  }, [weeklySummaries]);

  return <SelectSingle options={options} selectedId={selectedId} onChange={onChange} />;
};

const toSummarizedAtLabel = (summarized_at: string | undefined) => {
  if (summarized_at) return moment(summarized_at).format('YYYY/MM/DD HH:mm');
  return '';
};

const CreateWeeklySummaryButton = () => {
  const { _w_ } = useCurrentWorkspace();
  const { setShowLoading } = useLoading();
  const { mutate, isPending, error, isSuccess } = useCreateWeeklySummary();
  const { invalidate: invalidateWeeklySummaries } = useInvalidateWeeklySummaries({ identifier: _w_!.identifier });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const handleClick = () => {
    mutate({ identifier: _w_!.identifier });
  };

  useEffect(() => {
    if (isSuccess) {
      invalidateWeeklySummaries();
      toastSuccess('集計の作成が完了しました');
    }
  }, [isSuccess]);

  return (
    <div className="d-flex mb-4 align-items-center justify-content-end">
      <button className="btn bg-gradient-primary btn-icon mb-0" onClick={handleClick} disabled={isPending}>
        <i className="fa fa-plus"></i> 現時点の集計を作成
      </button>
    </div>
  );
};
